import { useCallback, useEffect, useRef, useState } from 'react';

import { Document, Page, pdfjs } from 'react-pdf';
import classNames from 'classnames';
import panzoom, { PanZoom } from 'panzoom';
import { useResizeDetector } from 'react-resize-detector';

import { useBlobUrl } from '../hooks';
import { usePdfWorkerSrc } from '../services/hooks';
import { CircularProgress } from '../CircularProgress';
import { Box } from '../Box';
import { Dialog } from '../Dialog';
import { ImageWithLoader } from '../ImageWithLoader';

import { usePanZoomBoundaries } from './hooks';
import { PreviewButtons } from './PreviewButtons';
import { useStyles } from './FilePreviewWithZoom.styles';

const options = {
  isEvalSupported: false,
};

/**
 * Свойства компонента {@link FilePreviewWithZoom}
 */
export interface FilePreviewWithZoomProps {
  /**
   * Если true, диалоговое окно открыто.
   */
  open: boolean;

  /**
   * Функция обратного вызова для обработки закрытия диалогового окна.
   */
  onClose: () => void;

  /**
   * URL файла PDF для предварительного просмотра.
   */
  url: string;

  /**
   * Функция для загрузки полной версии файла.
   */
  onLoadFull?: () => Promise<Blob>;

  /**
   * Коллбэк-функция для обработки успешной загрузки файла.
   */
  onLoadSuccess?: () => void;

  /**
   * Если true, то появляется для кнопок снизу.
   */
  withBottomOffset?: boolean;

  /**
   * Тип файла для отображения.
   *
   * @defaultValue `'pdf'`
   */
  fileType?: 'image' | 'pdf';

  /**
   * Если true, то использует альтернативный компонент для загрузки изображения.
   */
  alternativeImageComponent?: boolean;
}

/**
 * Компонент для предварительного просмотра файла с использованием диалогового окна c масштабированием.
 *
 * @public
 * @param open - Если true, диалоговое окно открыто.
 * @param onClose - Функция обратного вызова для обработки закрытия диалогового окна.
 * @param url - URL файла PDF для предварительного просмотра.
 * @param onLoadFull - Функция для загрузки полной версии файла.
 * @param onLoadSuccess - Коллбэк-функция для обработки успешной загрузки файла.
 * @param withBottomOffset - Если true, то появляется для кнопок снизу.
 * @param fileType - Тип файла для отображения.
 * @param alternativeImageComponent - Если true, то использует альтернативный компонент для загрузки изображения.
 * @returns  JSX-элемент, представляющий компонент предварительного просмотра файла в масштабируемом диалоговом окне.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FilePreviewWithZoomProps}
 */
export const FilePreviewWithZoom = ({
  open,
  onClose,
  onLoadSuccess,
  onLoadFull,
  url,
  withBottomOffset,
  fileType = 'pdf',
  alternativeImageComponent = false,
}: FilePreviewWithZoomProps) => {
  const panZoomRef = useRef<PanZoom | null>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement | null>(
    null,
  );
  const [objectUrl, setObjectUrl] = useState(url);

  const setPaperRef = useCallback((node: HTMLDivElement | null) => {
    paperRef.current = node;
    setDialogContainer((node?.parentElement as HTMLElement | null) ?? null);
  }, []);

  const pdfWorkerSrc = usePdfWorkerSrc();

  const { createUrl } = useBlobUrl();

  if (pdfjs.GlobalWorkerOptions.workerSrc !== pdfWorkerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;
  }

  const { classes } = useStyles();

  const zoomLevels = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3, 4, 5];
  const [currentZoom, setCurrentZoom] = useState(1);

  const [pagesCount, setPagesCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const { checkBoundaries, setupBoundaryCheck } = usePanZoomBoundaries(
    panZoomRef,
    paperRef,
  );
  const { width } = useResizeDetector<HTMLElement>({
    targetRef: { current: dialogContainer },
    refreshMode: 'debounce',
    refreshRate: 100,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadFile = async () => {
      try {
        if (onLoadFull) {
          const file = await onLoadFull();

          if (!isCancelled) {
            setObjectUrl(createUrl(file));
          }
        }
      } catch (e) {}
    };

    loadFile();

    return () => {
      isCancelled = true;
    };
  }, [onLoadFull, createUrl]);

  const reset = () => {
    if (panZoomRef.current) {
      setCurrentZoom(1);
      setPageNumber(1);
      panZoomRef.current.dispose();
      panZoomRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [url]);

  const zoomTo = (scale: number) => {
    if (!paperRef.current || !panZoomRef.current) return;

    const { offsetWidth, offsetHeight } = paperRef.current;
    const centerX = offsetWidth / 2;
    const centerY = offsetHeight / 2;
    panZoomRef.current.smoothZoomAbs(centerX, centerY, scale);
  };

  const handleLoadSuccess = useCallback(
    ({ numPages }: { numPages?: number }) => {
      setPagesCount(numPages || 1);
      requestAnimationFrame(() => {
        if (!open || !paperRef.current) return;

        panZoomRef.current = panzoom(paperRef.current, {
          maxZoom: 5,
          minZoom: 0.25,
          transformOrigin: { x: 0, y: 0 },
          initialZoom: 1,
          beforeWheel: () => true,
          beforeMouseDown: (event) =>
            !paperRef.current?.contains(event.target as Node),
          smoothScroll: false,
          zoomDoubleClickSpeed: 1,
        });

        onLoadSuccess?.();
      });
    },
    [open, onLoadSuccess, setPagesCount],
  );

  setupBoundaryCheck();

  const handleZoom = (scale: number) => {
    zoomTo(scale);
    setTimeout(() => {
      checkBoundaries();
    }, 300);
    setCurrentZoom(scale);
  };

  if (!open) return null;

  const renderDocument = (pageWidth?: number) => {
    if (!pageWidth) {
      return (
        <Box display="flex" justifyContent="center" p="32">
          <CircularProgress size={50} />
        </Box>
      );
    }

    return (
      <Document
        options={options}
        file={url}
        className={classNames(classes.document)}
        error={
          <Box p="32" color="text.light.secondary" textAlign="center">
            Ошибка при загрузке файла
          </Box>
        }
        loading={
          <Box display="flex" justifyContent="center" p="32">
            <CircularProgress size={50} />
          </Box>
        }
        onLoadSuccess={handleLoadSuccess}
      >
        <Page
          width={pageWidth}
          scale={Math.max(1.5, currentZoom)}
          pageNumber={pageNumber}
          renderAnnotationLayer
          loading={
            <Box display="flex" justifyContent="center" p="32">
              <CircularProgress size={50} />
            </Box>
          }
        />
      </Document>
    );
  };

  const renderImage = () => (
    <Box>
      <div className={classes.imageWrapper} />
      <img
        className={classNames(classes.image)}
        src={objectUrl}
        alt=""
        onLoad={() => handleLoadSuccess({})}
      />
    </Box>
  );

  // TODO: в рамках будущей задачи удалить renderImage и использовать только этот
  const renderImageWithLoader = () => (
    <ImageWithLoader
      objectFit="contain"
      src={objectUrl}
      height="100%"
      width="100%"
      onLoad={() => handleLoadSuccess({})}
    />
  );

  return (
    <Box>
      <Dialog
        scroll="body"
        open={open}
        onClose={onClose}
        classes={{
          paper: classes.dialogPaper,
        }}
        slotProps={{
          paper: {
            ref: setPaperRef,
          },
        }}
      >
        <Box>
          {fileType === 'pdf' && renderDocument(width)}
          {fileType === 'image' && !alternativeImageComponent && renderImage()}
          {fileType === 'image' &&
            alternativeImageComponent &&
            renderImageWithLoader()}
        </Box>
      </Dialog>
      <PreviewButtons
        handleNextPage={setPageNumber}
        pagesCount={pagesCount}
        nextPageNumber={pageNumber}
        pageNumber={pageNumber}
        scale={currentZoom}
        zoomLevels={zoomLevels}
        handleZoom={handleZoom}
        onClose={onClose}
        withBottomOffset={withBottomOffset}
      />
    </Box>
  );
};
